/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { object, node, bool, oneOf } from 'prop-types';
import { get } from 'lodash';
import { useMediaLayout } from 'use-media';
import LineItemCustomerCommissionMaybe from './LineItems/LineItemCustomerCommissionMaybe';
import LineItemProviderCommissionMaybe from './LineItems/LineItemProviderCommissionMaybe';
import LineItemRefundMaybe from './LineItems/LineItemRefundMaybe';
import LineItemTotalPrice from './LineItems/LineItemTotalPrice';
import LineItemPackageItem from './LineItems/LineItemPackageItem';
import LineItemFlexiblePricing from './LineItems/LineItemFlexiblePricing';
import LineItemSpecialOfferMaybe from './LineItems/LineItemSpecialOfferMaybe';
import LineItemCustomerLodgingTaxMaybe from './LineItems/LineItemCustomerLodgingTaxMaybe';
import LineItemInsurance from './LineItems/LineItemInsurance';
import LineItemLodgingFee from './LineItems/LineItemLodgingFee';
import LineItemConservationDonationMaybe from './LineItems/LineItemConservationDonationMaybe';
import LineItemPromoCode from './LineItems/LineItemPromoCode';
import LineItemGuestLiabilityProtection from '../LineItemGuestLiabilityProtection/LineItemGuestLiabilityProtection';
import LineItemLtPlusCheckoutPreview from '../LineItemGuestLiabilityProtection/LineItemLtPlusCheckoutPreview';
import LineItemLtPlusMembership from '../LineItemGuestLiabilityProtection/LineItemLtPlusMembership';
import {
  isGlpLtPlusSelection,
  GLP_SELECTION,
  normalizeGlpSelection,
} from '../../util/guestLiabilityProtection';
import {
  LINE_ITEM_LODGING_FEE,
  LINE_ITEM_NEGOTIATION,
  LINE_ITEM_SPECIAL_OFFER,
} from '../../util/types';
import {
  findPackageOrDiyLineItem,
  getBookingDates,
  getLineItemByCode,
  txIsDelivered,
  txIsDeclined,
  txIsCancelled,
  getPartySizeGuestCount,
} from '../../util/transaction';
import { formatMoney } from '../../util/currency';
import { daysAdded } from '../../util/dates';

import { types as sdkTypes } from '../../util/sdkLoader';
import PricingDetailsCard from '../PricingDetails/PricingDetailsCard';

import css from './PricingDetails.module.css';

const { Money } = sdkTypes;

const PricingDetailsComponent = props => {
  const { isInitial, hasLodging } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const isWide = useMediaLayout({ minWidth: '768px' });
  const {
    transaction,
    userRole,
    intl,
    isSpecialOffer: specialOfferEstimate,
    totalLabel,
    glpSelection,
    isGuestLiabilityProtectionIncluded,
  } = props;

  const glpSelectionFromMetadata = get(
    transaction,
    'attributes.metadata.guestLiabilityProtectionSelection'
  );
  const effectiveGlpSelection = normalizeGlpSelection(glpSelection ?? glpSelectionFromMetadata);

  const protectedData = get(transaction, 'attributes.protectedData', null);
  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';

  const emailLineItems = get(protectedData, 'emailLineItems', []);
  const isEstimate = transaction?.attributes?.protectedData?.isEstimate || false;
  const isPackage = !!(protectedData && protectedData.packageLineItem);
  const guestSize = get(transaction, 'attributes.protectedData.packageLineItem.guestSize', 1);

  const isSpecialOffer =
    getLineItemByCode(transaction, LINE_ITEM_NEGOTIATION).length > 0 ||
    getLineItemByCode(transaction, LINE_ITEM_SPECIAL_OFFER).length > 0 ||
    specialOfferEstimate;

  const lodgingFeeLineItem =
    getLineItemByCode(transaction, LINE_ITEM_LODGING_FEE).length > 0 &&
    getLineItemByCode(transaction, LINE_ITEM_LODGING_FEE)[0];

  const { bookingStart, bookingEnd, lodgingStartDate } = getBookingDates(transaction);

  const lodgingFeeLineItemInfo = lineItem => {
    const formattedLodgingFeeTotal = lineItem?.unitPrice && formatMoney(intl, lineItem?.unitPrice);
    let nights = daysAdded(bookingStart, bookingEnd) - 1;

    if (isInitial && hasLodging) {
      nights += 1;
    }

    const lodgingAmount = get(lineItem, 'unitPrice.amount', 0);

    const dailyLodgingPrice =
      nights > 0 && formatMoney(intl, new Money(lodgingAmount / nights, 'USD'));

    const lodgingFeeLabel = formattedLodgingFeeTotal && dailyLodgingPrice && (
      <FormattedMessage
        id="LineItemLodgingFee.nightCount"
        values={{
          price: dailyLodgingPrice,
          nights,
        }}
      />
    );
    return { lineItem, lodgingFeeLabel, formattedLodgingFeeTotal };
  };

  const packageOrDiyLineItem = findPackageOrDiyLineItem(transaction);

  // Helper function to calculate day count with lodging adjustments
  const calculateDayCount = () => {
    let dayCount = daysAdded(bookingStart, bookingEnd);

    if (lodgingStartDate && lodgingStartDate.isSame(bookingStart) && dayCount > 1 && !isInitial) {
      dayCount -= 1;
    }

    return dayCount;
  };

  const transformPackageLineItems = (lineItems, quantity, isFlexiblePricing = false) => {
    return lineItems
      .map(lineItem => {
        const filteredlineItem = emailLineItems.find(
          emailLineItem => emailLineItem.code === lineItem.code
        );

        const itemQuantity = isFlexiblePricing
          ? filteredlineItem?.guestSize ?? 1
          : quantity || lineItem.quantity.toNumber();

        // Package Info
        try {
          const dayCount = calculateDayCount();

          let estimatedTotal;

          if (isFlexiblePricing) {
            estimatedTotal = new Money(lineItem?.lineTotal?.amount, 'USD');
          } else {
            estimatedTotal = new Money(
              lineItem?.lineTotal?.amount * itemQuantity * (isFlexiblePricing ? 1 : dayCount),
              'USD'
            );
          }

          const formattedPackageTotal =
            lineItem?.lineTotal &&
            formatMoney(intl, isEstimate ? estimatedTotal : lineItem?.lineTotal);

          // Line Item info for packages
          const formattedlinePrice = lineItem?.unitPrice && formatMoney(intl, lineItem?.unitPrice);

          const packageLabel = formattedlinePrice && itemQuantity && (
            <FormattedMessage
              id="LineItemPackageItem.guestCount"
              values={{
                price: formattedlinePrice,
                guestSize: itemQuantity,
                dayCount,
              }}
            />
          );

          return { lineItem, packageLabel, formattedPackageTotal, dayCount, itemQuantity };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          return null;
        }
      })
      .filter(Boolean);
  };

  const flexiblePricingLineItems = transaction?.attributes?.lineItems?.filter(
    lineItem =>
      lineItem.code?.startsWith('line-item/adult-') || lineItem.code?.startsWith('line-item/youth-')
  );

  const hasFlexiblePricing = flexiblePricingLineItems.length > 0;

  // Special Offer on mobile is already in a <Modal/>
  // Modals can't render the tooltip
  const modalOffer = !isWide && isSpecialOffer && isEstimate;
  let transformedPackageLineItems;

  if (flexiblePricingLineItems?.length) {
    transformedPackageLineItems = transformPackageLineItems(flexiblePricingLineItems, null, true);
  } else {
    transformedPackageLineItems = transformPackageLineItems(packageOrDiyLineItem, guestSize);
  }

  // Get total price from transaction
  const totalPrice = isCustomer
    ? transaction?.attributes?.payinTotal
    : transaction?.attributes?.payoutTotal;

  const formattedTotalPrice = totalPrice && formatMoney(intl, totalPrice);

  // Get guest count from transaction
  let guestCount;
  if (typeof transaction?.attributes?.protectedData?.partySize === 'object') {
    guestCount = (isSpecialOffer && getPartySizeGuestCount(transaction)) || guestSize;
  } else if (transaction?.attributes?.protectedData?.partySize) {
    guestCount = transaction.attributes.protectedData.partySize;
  } else {
    guestCount = guestSize;
  }

  const dayCount = calculateDayCount();

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  useEffect(() => {
    if (isGlpLtPlusSelection(effectiveGlpSelection)) {
      setIsExpanded(true);
    }
  }, [effectiveGlpSelection]);

  let summarySubTextLabel = (
    <FormattedMessage
      id="PricingDetails.summarySubtext"
      values={{
        dayCount,
        guestCount,
      }}
      defaultMessage="All fees included for {dayCount} {dayCount, plural, one {Day} other {Days}} • {guestCount} {guestCount, plural, one {Guest} other {Guests}}"
    />
  );

  if (isProvider) {
    let providerTotalMessageId = 'BookingBreakdown.providerTotalDefault';
    if (txIsDelivered(transaction)) {
      providerTotalMessageId = 'BookingBreakdown.providerTotalDelivered';
    } else if (txIsDeclined(transaction)) {
      providerTotalMessageId = 'BookingBreakdown.providerTotalDeclined';
    } else if (txIsCancelled(transaction)) {
      providerTotalMessageId = 'BookingBreakdown.providerTotalCancelled';
    }

    summarySubTextLabel = <FormattedMessage id={providerTotalMessageId} />;
  }

  return (
    <PricingDetailsCard
      isExpanded={isExpanded}
      onToggle={toggleExpanded}
      ariaLabel="Toggle pricing details"
      summary={
        <div className={css.summaryPrice}>
          {formattedTotalPrice}
          <div className={css.summarySubtext}>{summarySubTextLabel}</div>
        </div>
      }
    >
      {/* package or special offer lineItems */}
      {isSpecialOffer && <LineItemSpecialOfferMaybe transaction={transaction} intl={intl} />}

      {/* flexible pricing lineItems */}
      {hasFlexiblePricing && (
        <LineItemFlexiblePricing lineItems={transformedPackageLineItems} intl={intl} />
      )}

      {isPackage && !hasFlexiblePricing && (
        <LineItemPackageItem itemInfo={transformedPackageLineItems} />
      )}

      {/* lodging fee lineItem */}
      {lodgingFeeLineItem && (
        <LineItemLodgingFee itemInfo={lodgingFeeLineItemInfo(lodgingFeeLineItem)} />
      )}

      {/* commission lineItems */}
      <LineItemCustomerCommissionMaybe
        transaction={transaction}
        isCustomer={isCustomer}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        transaction={transaction}
        isProvider={isProvider}
        intl={intl}
        modalOffer={modalOffer}
      />

      {/* extra lineItems */}
      <LineItemCustomerLodgingTaxMaybe
        transaction={transaction}
        isCustomer={isCustomer}
        intl={intl}
      />
      <LineItemConservationDonationMaybe
        transaction={transaction}
        isCustomer={isCustomer}
        intl={intl}
      />
      <LineItemInsurance transaction={transaction} isCustomer={isCustomer} intl={intl} />
      {isGlpLtPlusSelection(effectiveGlpSelection) && isCustomer ? (
        <LineItemLtPlusCheckoutPreview
          transaction={transaction}
          isCustomer={isCustomer}
          intl={intl}
        />
      ) : (
        <LineItemGuestLiabilityProtection
          transaction={transaction}
          isCustomer={isCustomer}
          intl={intl}
          forceWaived={isGuestLiabilityProtectionIncluded}
        />
      )}
      <LineItemPromoCode transaction={transaction} isCustomer={isCustomer} intl={intl} />
      {/* refund lineItem */}
      <LineItemRefundMaybe transaction={transaction} intl={intl} userRole={userRole} />
      {/* total lineItem */}
      <LineItemTotalPrice
        transaction={transaction}
        isProvider={isProvider}
        intl={intl}
        totalLabel={totalLabel}
      />
      {isGlpLtPlusSelection(effectiveGlpSelection) && isCustomer && (
        <>
          <hr className={css.membershipDivider} />
          <LineItemLtPlusMembership transaction={transaction} isCustomer={isCustomer} intl={intl} />
        </>
      )}
    </PricingDetailsCard>
  );
};

PricingDetailsComponent.defaultProps = {
  totalLabel: null,
  isInitial: false,
  glpSelection: undefined,
  isGuestLiabilityProtectionIncluded: false,
};

PricingDetailsComponent.propTypes = {
  transaction: object.isRequired,
  userRole: oneOf(['provider', 'customer']).isRequired,
  intl: intlShape.isRequired,
  totalLabel: node,
  isInitial: bool,
  glpSelection: oneOf([GLP_SELECTION.YES, GLP_SELECTION.NO, GLP_SELECTION.LT_PLUS]),
  isGuestLiabilityProtectionIncluded: bool,
};

const PricingDetails = injectIntl(PricingDetailsComponent);

PricingDetails.displayName = 'PricingDetails';

export default PricingDetails;
